import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Seguimiento del mouse para los fondos del hero: escribe --mx / --my (0–1)
 * como CSS vars en el contenedor; los presets las leen con calc(). Solo en
 * desktop con puntero fino y sin prefers-reduced-motion — en mobile y con
 * reduced-motion los fondos quedan estáticos (los presets también apagan sus
 * keyframes vía CSS).
 */
export function useHeroPointer<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const reduce = useReducedMotion();
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    if (reduce || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px) and (pointer: fine)');
    const update = () => setInteractive(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, [reduce]);

  const onMouseMove = useCallback(
    (e: React.MouseEvent<T>) => {
      if (!interactive || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const mx = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const my = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
      ref.current.style.setProperty('--mx', mx.toFixed(3));
      ref.current.style.setProperty('--my', my.toFixed(3));
    },
    [interactive]
  );

  const onMouseLeave = useCallback(() => {
    ref.current?.style.setProperty('--mx', '0.5');
    ref.current?.style.setProperty('--my', '0.5');
  }, []);

  return { ref, onMouseMove, onMouseLeave, interactive, reduce: !!reduce };
}
